import React, { useCallback, useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Edit2 } from 'react-feather'
import moment from 'moment'
import ClipLoader from 'react-spinners/ClipLoader'
import {
  FilterOption,
  GrantData,
  GrantFilterValue,
  GrantListPage,
  GrantProgram,
  GranteeType,
  UserData,
  defaultGrantsPage,
} from '../types'
import { useImportGrantFileMutation, useSearchGrantsMutation } from '../store/api'
import { connect } from 'react-redux'
import PrivateLayout from '../components/PrivateLayout'
import Footer from '../components/Footer'
import OrganizationSummary from '../components/OrganizationSummary'
import { formatPrice } from '../utils'
import uploadIcon from '../assets/scss/img/icons/cloud-upload.svg'
import { setGrantsPage } from '../store/slices/grantSlice'

interface GrantListPageStateProps {
  user: UserData
  grantsPage: GrantListPage
}

interface GrantListPageDispatchProps {
  setGrantsPage: (grantsPage: GrantListPage) => void
}

type GrantListPageProps = GrantListPageStateProps & GrantListPageDispatchProps

const ITEMS_PER_PAGE = 10

const grantProgramOptions: FilterOption[] = [
  {
    label: '',
    value: 'all',
  },
  {
    label: GrantProgram.HumilityInTheology,
    value: GrantProgram.HumilityInTheology,
  },
  {
    label: GrantProgram.CharacterVirtueDevelopment,
    value: GrantProgram.CharacterVirtueDevelopment,
  },
  {
    label: GrantProgram.HumanSciences,
    value: GrantProgram.HumanSciences,
  },
  {
    label: GrantProgram.VirtuesAndWellBeing,
    value: GrantProgram.VirtuesAndWellBeing,
  },
]

const granteeTypeOptions: FilterOption[] = [
  {
    label: '',
    value: GranteeType.All,
  },
  {
    label: GranteeType.NonProfitOrganization,
    value: GranteeType.NonProfitOrganization,
  },
  {
    label: GranteeType.EducationalInstitution,
    value: GranteeType.EducationalInstitution,
  },
  {
    label: GranteeType.GovernementAgency,
    value: GranteeType.GovernementAgency,
  },
  {
    label: GranteeType.Individual,
    value: GranteeType.Individual,
  },
]

const defaultGrantFilterPanel: GrantFilterValue = {
  programTheme: grantProgramOptions[0].value,
  grantYear: 'all',
  organizationType: granteeTypeOptions[0].value,
}

const GrantListPageComponent = ({ user, grantsPage, setGrantsPage }: GrantListPageProps): JSX.Element => {
  const navigate = useNavigate()

  const [sortField, setSortField] = useState<string>('')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [showAllItems, setShowAllItems] = useState<boolean>(false)
  const [searchValue, setSearchValue] = useState('')
  const [pageNumber, setPageNumber] = useState(0)
  const [filterValue, setFilterValue] = useState<GrantFilterValue>(defaultGrantFilterPanel)
  const [isImportApiRequestInProgress, setIsImportApiRequestInProgress] = useState<boolean>(false)

  const [isGetGrantsApiRequestInProgress, setIsGetGrantsApiRequestInProgress] = useState<boolean>(false)

  const [importGrantFile] = useImportGrantFileMutation()
  const [searchGrants] = useSearchGrantsMutation()

  const isFunder = useMemo(() => {
    return user && user.roles.includes('ROLE_FUNDER')
  }, [user])

  const refreshGrantData = useCallback(async () => {
    const sortString = sortField && sortDirection ? [sortDirection, sortField] : ['asc', 'id']

    try {
      setIsGetGrantsApiRequestInProgress(true)

      await searchGrants({
        data: {
          page: pageNumber,
          size: showAllItems ? 1000 : ITEMS_PER_PAGE,
          filters: filterValue,
          sort: sortString,
          title: searchValue,
        },
      }).unwrap()

      setIsGetGrantsApiRequestInProgress(false)
    } catch (e) {
      console.error(e)
      setIsGetGrantsApiRequestInProgress(false)
    }
  }, [showAllItems, pageNumber, filterValue, sortField, sortDirection, searchValue])

  useEffect(() => {
    setGrantsPage(defaultGrantsPage)
    refreshGrantData()
  }, [])

  useEffect(() => {
    refreshGrantData()
  }, [searchValue, sortField, sortDirection, showAllItems, pageNumber])

  const onFilterPanelApplied = useCallback(() => {
    refreshGrantData()
  }, [refreshGrantData])

  const resetFilters = useCallback(() => {
    setFilterValue({
      ...defaultGrantFilterPanel,
    })
    setTimeout(() => {
      refreshGrantData()
    })
  }, [refreshGrantData])

  const onPrevPageClick = useCallback(() => {
    if (grantsPage?.first) {
      return
    }

    setPageNumber(pageNumber - 1)
  }, [pageNumber])

  const onNextPageClick = useCallback(() => {
    if (grantsPage?.last) {
      return
    }

    setPageNumber(pageNumber + 1)
  }, [pageNumber])

  const grantYearOptions = useMemo(() => {
    const options: FilterOption[] = [
      {
        label: '',
        value: 'all',
      },
    ]

    const currentYear: number = new Date().getFullYear()

    for (let i = 0; i < 5; i++) {
      const yearValue = String(currentYear - i)

      options.push({
        label: yearValue,
        value: yearValue,
      })
    }

    return options
  }, [])

  const grants: GrantData[] = useMemo(() => {
    return grantsPage?.content
  }, [grantsPage])

  const getGrantStatusClass = useCallback((status?: string) => {
    const value = (status ?? '').toLowerCase()

    switch (value) {
      case 'closed':
        return 'badge-phoenix-success'
      case 'open':
        return 'badge-phoenix-primary'
      case 'in progress':
        return 'badge-phoenix-info'
      case 'cancelled':
        return 'badge-phoenix-danger'
      default:
        return ''
    }
  }, [])

  const onImportFileChange = useCallback(async (event: { target: HTMLInputElement }) => {
    if (event && event.target && event.target.files && event.target.files[0]) {
      const file = event.target.files[0]
      const formData = new FormData()
      formData.append('files', file, file.name)

      try {
        setIsImportApiRequestInProgress(true)
        await importGrantFile(formData).unwrap()
        setIsImportApiRequestInProgress(false)
        toast('XML data has been successfully imported')
        refreshGrantData()
      } catch (e) {
        setIsImportApiRequestInProgress(false)
        console.dir(e)
      }
    }
  }, [])

  return (
    <PrivateLayout>
      <div className="content">
        <OrganizationSummary />

        <div className="row mt-3">
          <div className="col-12">
            <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white pt-6 border-top border-300">
              <div id="projectSummary">
                <div className="row align-items-end justify-content-between pb-4 g-3">
                  <div className="col-auto d-flex align-items-center">
                    <h3>Grants</h3>
                    {isGetGrantsApiRequestInProgress ? (
                      <div className="spinner-wrapper ms-3" style={{ width: 'unset' }}>
                        <ClipLoader color="#e5780b" loading={isGetGrantsApiRequestInProgress} size={20} />
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="row align-items-center g-3 justify-content-between justify-content-start mb-3 pb-3">
                  <div className="col-auto">
                    <div className="d-flex">
                      <div className="search-box me-2 d-none d-xl-block">
                        <form className="position-relative" data-bs-toggle="search" data-bs-display="static">
                          <input
                            className="form-control search-input search"
                            type="search"
                            placeholder="Search Grant"
                            aria-label="Search"
                            value={searchValue}
                            onChange={(e) => {
                              setSearchValue(e.target.value)
                            }}
                          />
                          <span className="fas fa-search search-box-icon"></span>
                        </form>
                      </div>
                      <button className="btn px-3 btn-phoenix-secondary me-2 d-xl-none">
                        <span className="fa-solid fa-search"></span>
                      </button>
                      <button
                        className="btn px-3 btn-phoenix-primary"
                        type="button"
                        data-bs-toggle="modal"
                        data-bs-target="#filterModal"
                        data-boundary="window"
                        aria-haspopup="true"
                        aria-expanded="false"
                        data-bs-reference="parent"
                      >
                        <span className="fa-solid fa-filter" data-fa-transform="down-3"></span>
                      </button>
                      <div className="modal fade" id="filterModal" tabIndex={-1}>
                        <div className="modal-dialog modal-dialog-centered">
                          <div className="modal-content border">
                            <form id="addEventForm" autoComplete="off">
                              <div className="modal-header border-200 p-4">
                                <h5 className="modal-title text-1000 fs-2 lh-sm">Filter</h5>
                                <button
                                  className="btn p-1 text-900"
                                  type="button"
                                  data-bs-dismiss="modal"
                                  aria-label="Close"
                                >
                                  <span className="fas fa-times fs--1"></span>
                                </button>
                              </div>
                              <div className="modal-body pt-4 pb-2 px-4">
                                <div className="mb-3">
                                  <label className="fw-bold mb-2 text-1000" htmlFor="programTheme">
                                    Grant Program
                                  </label>
                                  <select
                                    className="form-select"
                                    id="programTheme"
                                    value={filterValue.programTheme}
                                    onChange={(e) => {
                                      setFilterValue((currentValue) => ({
                                        ...currentValue,
                                        programTheme: e.target.value,
                                      }))
                                    }}
                                  >
                                    {grantProgramOptions.map((option) => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="mb-3">
                                  <label className="fw-bold mb-2 text-1000" htmlFor="grantYear">
                                    Grant Year
                                  </label>
                                  <select
                                    className="form-select"
                                    id="grantYear"
                                    value={filterValue.grantYear}
                                    onChange={(e) => {
                                      setFilterValue((currentValue) => ({
                                        ...currentValue,
                                        grantYear: e.target.value,
                                      }))
                                    }}
                                  >
                                    {grantYearOptions.map((option) => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="mb-3">
                                  <label className="fw-bold mb-2 text-1000" htmlFor="organizationType">
                                    Grantee Type
                                  </label>
                                  <select
                                    className="form-select"
                                    id="organizationType"
                                    value={filterValue.organizationType}
                                    onChange={(e) => {
                                      setFilterValue((currentValue) => ({
                                        ...currentValue,
                                        organizationType: e.target.value,
                                      }))
                                    }}
                                  >
                                    {granteeTypeOptions.map((option) => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              <div className="modal-footer d-flex justify-content-end align-items-center px-4 pb-4 border-0 pt-3">
                                <button
                                  className="btn btn-sm btn-phoenix-primary px-4 fs--2 my-0"
                                  type="button"
                                  onClick={resetFilters}
                                >
                                  {' '}
                                  <span className="fas fa-arrows-rotate me-2 fs--2"></span>Reset
                                </button>
                                <button
                                  className="btn btn-sm btn-primary px-9 fs--2 my-0"
                                  type="button"
                                  onClick={onFilterPanelApplied}
                                  data-bs-dismiss="modal"
                                  aria-label="Close"
                                >
                                  Done
                                </button>
                              </div>
                            </form>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-auto">
                    <div className="d-flex align-items-center">
                      {isFunder ? (
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            navigate('/grants/add')
                          }}
                        >
                          <span className="fas fa-plus me-2"></span>Add new grant
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>

                {isGetGrantsApiRequestInProgress ? null : (
                  <>
                    <div className="table-responsive ms-n1 ps-1 scrollbar">
                      {grants.length ? (
                        <table className="table fs--1 mb-0 border-top border-200">
                          <thead>
                            <tr>
                              <th
                                className="sort align-middle ps-3"
                                scope="col"
                                data-sort="grantnumber"
                                style={{ width: '15%' }}
                              >
                                Grant Number
                              </th>
                              <th
                                className="sort white-space-nowrap align-middle ps-3"
                                scope="col"
                                data-sort="projecttitle"
                                style={{ width: '30%' }}
                              >
                                Project Title
                              </th>
                              <th
                                className="sort align-middle ps-3"
                                scope="col"
                                data-sort="approveddate"
                                style={{ width: '10%' }}
                              >
                                Approved Date
                              </th>
                              <th
                                className="sort align-middle ps-3"
                                scope="col"
                                data-sort="grantedamount"
                                style={{ width: '10%' }}
                              >
                                Granted Amount
                              </th>
                              <th
                                className="sort align-middle ps-3"
                                scope="col"
                                data-sort="status"
                                style={{ width: '5%' }}
                              >
                                Status
                              </th>
                              <th
                                className="sort align-middle ps-3"
                                scope="col"
                                data-sort="primarycontact"
                                style={{ width: '10%' }}
                              >
                                Primary Contact
                              </th>
                              <th
                                className="sort align-middle ps-3"
                                scope="col"
                                data-sort="doi"
                                style={{ width: '20%' }}
                              >
                                Email
                              </th>
                              <th
                                className="sort align-middle ps-3"
                                scope="col"
                                data-sort="type"
                                style={{ width: '10%' }}
                              >
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="list" id="project-summary-table-body">
                            {grants.map((grant) => (
                              <tr key={grant.id} className="position-static">
                                <td className="align-middle time white-space-nowrap ps-3 grantnumber">
                                  <button
                                    type="button"
                                    className="btn-link fw-normal fs--1"
                                    onClick={() => {
                                      navigate(`/grants/${grant.id}/details`)
                                    }}
                                    style={{ color: '#3874ff' }}
                                  >
                                    {grant.externalId ?? ''}
                                  </button>
                                </td>
                                <td
                                  className="align-middle white-space-nowrap fs--1 projecttitle ps-3"
                                  style={{ maxWidth: '400px', whiteSpace: 'normal', textAlign: 'left' }}
                                >
                                  {grant.projectTitle}
                                </td>
                                <td className="align-middle white-space-nowrap approveddate ps-3">
                                  <p className="mb-0 fs--1 text-900">
                                    {grant.startDate ? moment(grant.startDate).format('MMM DD, YYYY') : ''}
                                  </p>
                                </td>
                                <td className="align-middle white-space-nowrap grantedamount ps-3">
                                  <p className="mb-0 fs--1 text-900">
                                    {grant.totalAmount ? `$${formatPrice(grant.totalAmount)}` : ''}
                                  </p>
                                </td>
                                <td className="align-middle white-space-nowrap ps-3 status">
                                  <span
                                    className={`badge badge-phoenix fs--2 ${getGrantStatusClass(grant.requestStatus)}`}
                                  >
                                    {grant.requestStatus ?? ''}
                                  </span>
                                </td>
                                <td className="align-middle white-space-nowrap primarycontact ps-3">
                                  <p className="mb-0 fs--1 text-900">{grant.primaryContactName ?? ''}</p>
                                </td>
                                <td className="align-middle white-space-nowrap primarycontact ps-3">
                                  <p className="mb-0 fs--1 text-900">{grant.primaryContactEmail ?? ''}</p>
                                </td>
                                <td className="align-middle white-space-nowrap ps-3 type">
                                  <div className="d-flex justify-content-center sort align-middle ps-3">
                                    <Edit2
                                      className="text-success-300 pointer"
                                      size={18}
                                      onClick={() => navigate(`/grants/${grant.id}/edit`)}
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="my-7 text-center">No Grants found</div>
                      )}
                    </div>
                    {grants.length && isFunder ? (
                      <div className="row align-items-center justify-content-between py-2 pe-0 fs--1">
                        <div className="col-auto d-flex">
                          {grantsPage?.totalElements > 1 ? (
                            <p
                              className="mb-0 d-none d-sm-block me-3 fw-semi-bold text-900"
                              data-list-info="data-list-info"
                            >
                              {pageNumber * ITEMS_PER_PAGE + 1} to{' '}
                              {pageNumber * ITEMS_PER_PAGE + grantsPage?.content.length}{' '}
                              <span className="text-600"> Items of </span>
                              {grantsPage?.totalElements}
                            </p>
                          ) : null}
                          {showAllItems ? (
                            <button
                              className="btn-link fw-semi-bold"
                              onClick={() => {
                                setShowAllItems(false)
                              }}
                            >
                              View Less<span className="fas fa-angle-right ms-1" data-fa-transform="down-1"></span>
                            </button>
                          ) : null}
                          {!showAllItems && grantsPage?.totalPages > 1 ? (
                            <button
                              className="btn-link fw-semi-bold"
                              onClick={() => {
                                setShowAllItems(true)
                                setPageNumber(0)
                              }}
                            >
                              View all<span className="fas fa-angle-right ms-1" data-fa-transform="down-1"></span>
                            </button>
                          ) : null}
                        </div>
                        {grantsPage?.totalPages > 1 ? (
                          <div className="col-auto d-flex">
                            <button
                              className="page-link"
                              data-list-pagination="prev"
                              disabled={grantsPage?.first}
                              onClick={() => {
                                onPrevPageClick()
                              }}
                            >
                              <span className="fas fa-chevron-left"></span>
                            </button>
                            <ul className="mb-0 pagination"></ul>
                            <button
                              className="page-link pe-0"
                              data-list-pagination="next"
                              disabled={grantsPage?.last}
                              onClick={() => {
                                onNextPageClick()
                              }}
                            >
                              <span className="fas fa-chevron-right"></span>
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {isFunder ? (
          <div className="row gy-3 mt-3 mb-6 justify-content-between">
            <h3 className="mb-1 text-1100">Import Grant Data</h3>
            <div className="import-button-wrapper">
              <div className="dz-message" data-dz-message="data-dz-message">
                <img className="me-2" src={uploadIcon} width="25" />
                Select the XML file
                {isImportApiRequestInProgress ? (
                  <div className="spinner-wrapper ms-3" style={{ width: 'unset' }}>
                    <ClipLoader color="#e5780b" loading={isImportApiRequestInProgress} size={15} />
                  </div>
                ) : null}
              </div>
              <input className="import-button" type="file" accept=".xml" onChange={onImportFileChange} />
            </div>
          </div>
        ) : null}

        <Footer />
      </div>
    </PrivateLayout>
  )
}

const mapStateToProps = (state: any): GrantListPageStateProps => {
  return {
    user: state.auth.user,
    grantsPage: state.grant.grantsPage,
  }
}

const mapDispatchToProps = (dispatch: any): GrantListPageDispatchProps => {
  return {
    setGrantsPage: (payload: GrantListPage) => dispatch(setGrantsPage(payload)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(GrantListPageComponent)
